import { statusPatch, doneAtPatch } from '../../extensions/bundled/task-manager/taskStatus';
import { subtasksOf } from '../../extensions/bundled/task-manager/subtasks';
import { descriptionPatch } from '../taskDescription';
import { aiCreatedMarks, mcpSource, aiEditedMarks } from '../../services/ai/aiAuthorship';
import { PERSONAL } from '../scopes';
import { mcpError } from '../errors';
import { capRefusalFor } from '../../plans/capRefusal';
import { countOwnBoardsAndTasks } from '../../extensions/bundled/task-manager/boardsStore';
import { scopeReport } from '../writeScope';
import { stageBoardRecords } from '../boardChanges';
import {
  newId,
  taskRecordFor,
  newTaskFields,
  nextTaskPosition,
  toWireRecord,
  nowIso,
  patchedRecord,
} from '../boardRecords';
import {
  boardRows,
  casWrite,
  checkDueDate,
  checkLabels,
  checkPosition,
  checkPriority,
  checkStatus,
  checkSubtasks,
  findBoard,
  findGroup,
  findTask,
  requireRev,
  targetScope,
} from './boardWriteChecks';

/** Add a card to a list. Every field of the record is written explicitly. */
export async function boardTaskCreate({
  board_id: boardId,
  list_id: listId,
  title,
  scope,
  description,
  format,
  priority,
  due_date: dueDate,
  labels,
  position,
  done,
  status,
  subtasks,
}, ctx = {}) {
  const wire = scope || PERSONAL;
  const badPriority = checkPriority(priority);
  if (badPriority) return badPriority;
  const badStatus = checkStatus(status, done);
  if (badStatus) return badStatus;
  const badDate = checkDueDate(dueDate);
  if (badDate) return badDate;
  const checklist = checkSubtasks(subtasks, []);
  if (checklist.error) return checklist.error;
  // Plans v2: a card on a personal board counts against the plan's task cap.
  if (wire === PERSONAL) {
    const cappedTasks = await capRefusalFor('tasks', async () => (await countOwnBoardsAndTasks()).tasks);
    if (cappedTasks) return mcpError('plan_limit', cappedTasks, { requiredPlan: 'basic' });
  }

  const target = targetScope(wire);
  if (target.error) return target.error;

  const rows = await boardRows(target.scopeKey, boardId);
  const boardRow = findBoard(rows, boardId);
  if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');
  const groupRow = findGroup(rows, listId);
  if (!groupRow) return mcpError('not_found', 'No list with that id on this board.');
  if (groupRow.parent_id !== boardRow.id) {
    return mcpError('invalid_parent', 'That list belongs to a different board.', {
      boardId: boardRow.id,
      listId: groupRow.id,
    });
  }
  const badLabels = checkLabels(boardRow, labels);
  if (badLabels) return badLabels;

  const tasks = rows.filter((r) => r.record_type === 'task');
  const inList = nextTaskPosition(tasks, groupRow.id);
  const badPosition = checkPosition(position, inList);
  if (badPosition) return badPosition;

  const task = newTaskFields({
    id: newId(),
    groupId: groupRow.id,
    position: position === undefined ? inList : position,
    title: String(title ?? ''),
    descriptionHtml: description === undefined ? '' : descriptionPatch(description, format).descriptionHtml,
    priority: priority === undefined ? 'medium' : priority,
    labels: labels === undefined ? [] : labels.map(String),
    dueDate: dueDate === undefined ? null : String(dueDate),
    done,
    status,
    subtasks: checklist.subtasks || [],
  });
  // AI Act Art. 50(2): written by an external agent (services/ai/aiAuthorship.js).
  Object.assign(task, aiCreatedMarks(task.descriptionHtml || '', mcpSource(ctx?.caller)));
  const record = taskRecordFor(boardRow.id, task);
  const out = await casWrite(target.scopeKey, record, null);
  if (out.error) return out.error;

  const result = {
    boardId: boardRow.id,
    listId: groupRow.id,
    id: task.id,
    title: task.title,
    position: task.position,
    scope: wire,
    rev: out.rev,
    ...scopeReport(wire),
  };
  return stageBoardRecords(result, target.scopeKey, [toWireRecord(record)]);
}

/**
 * Change a card's own fields. Read-modify-write: `comments`, `assigneeId`,
 * `ownerId` and anything else on the record are carried forward untouched.
 * Moving between lists is `board_task_move`, not this.
 */
export async function boardTaskUpdate({
  board_id: boardId,
  task_id: taskId,
  expected_rev: expectedRev,
  scope,
  title,
  description,
  format,
  priority,
  due_date: dueDate,
  labels,
  done,
  status,
  subtasks,
}, ctx = {}) {
  const wire = scope || PERSONAL;
  const missingRev = requireRev(expectedRev);
  if (missingRev) return missingRev;
  const badPriority = checkPriority(priority);
  if (badPriority) return badPriority;
  const badDate = checkDueDate(dueDate);
  if (badDate) return badDate;
  const badStatus = checkStatus(status, done);
  if (badStatus) return badStatus;

  const target = targetScope(wire);
  if (target.error) return target.error;

  const rows = await boardRows(target.scopeKey, boardId);
  const boardRow = findBoard(rows, boardId);
  if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');
  const taskRow = findTask(rows, taskId);
  if (!taskRow) return mcpError('not_found', 'No task with that id on this board.');
  const badLabels = checkLabels(boardRow, labels);
  if (badLabels) return badLabels;

  const patch = { updatedAt: nowIso() };
  if (title !== undefined) patch.title = String(title);
  if (description !== undefined) {
    Object.assign(patch, descriptionPatch(description, format, taskRow));
    // AI Act Art. 50(2): the content was rewritten by an external agent.
    Object.assign(patch, aiEditedMarks(patch.descriptionHtml, mcpSource(ctx?.caller)));
  }
  if (priority !== undefined) patch.priority = priority;
  if (dueDate !== undefined) patch.dueDate = dueDate === null ? null : String(dueDate);
  if (labels !== undefined) patch.labels = labels.map(String);
  if (done !== undefined) patch.done = !!done;
  // The status wins together with the `done` it implies; `done` alone keeps the
  // old meaning, and `taskStatusOf` reconciles a status it contradicts.
  if (status !== undefined) Object.assign(patch, statusPatch(status));
  // Closing sets the done date, reopening clears it — the app's own rule.
  Object.assign(patch, doneAtPatch(taskRow, patch, patch.updatedAt));
  // A full replacement, checked against the task's own list. Safe as a
  // replace only because this tool already requires `expected_rev`.
  const checklist = checkSubtasks(subtasks, subtasksOf(taskRow));
  if (checklist.error) return checklist.error;
  if (checklist.subtasks !== undefined) patch.subtasks = checklist.subtasks;

  const record = patchedRecord(taskRow, { data: patch });
  const out = await casWrite(target.scopeKey, record, expectedRev);
  if (out.error) return out.error;

  const result = {
    boardId: boardRow.id,
    id: taskRow.id,
    listId: record.parent_id === boardRow.id ? null : record.parent_id,
    scope: wire,
    rev: out.rev,
    ...scopeReport(wire),
  };
  return stageBoardRecords(result, target.scopeKey, [toWireRecord(record)]);
}

/**
 * Move a card into another list of the SAME board.
 *
 * Both halves of the placement are rewritten together. `parent_id` is what
 * `assembleBoard` places by; `data.groupId` is a mirror the render path reads
 * after assembly. Setting only one of them puts the card in no column at all.
 *
 * `expected_rev` is optional here on purpose: refusing a move because someone
 * retitled the card is a worse failure than losing a position that the id
 * tie-break already makes ambiguous.
 */
export async function boardTaskMove({
  board_id: boardId,
  task_id: taskId,
  list_id: listId,
  scope,
  position,
  expected_rev: expectedRev,
}) {
  const wire = scope || PERSONAL;
  const target = targetScope(wire);
  if (target.error) return target.error;

  const rows = await boardRows(target.scopeKey, boardId);
  const boardRow = findBoard(rows, boardId);
  if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');
  const taskRow = findTask(rows, taskId);
  if (!taskRow) return mcpError('not_found', 'No task with that id on this board.');
  const groupRow = findGroup(rows, listId);
  if (!groupRow) return mcpError('not_found', 'No list with that id on this board.');
  if (groupRow.parent_id !== boardRow.id) {
    return mcpError('invalid_parent', 'That list belongs to a different board.', {
      boardId: boardRow.id,
      listId: groupRow.id,
    });
  }

  const tasks = rows.filter((r) => r.record_type === 'task' && r.id !== taskRow.id);
  const inList = nextTaskPosition(tasks, groupRow.id);
  const badPosition = checkPosition(position, inList);
  if (badPosition) return badPosition;

  const record = patchedRecord(taskRow, {
    parent_id: groupRow.id,
    data: {
      groupId: groupRow.id,
      position: position === undefined ? inList : position,
      updatedAt: nowIso(),
    },
  });
  const out = await casWrite(target.scopeKey, record, expectedRev ?? null);
  if (out.error) return out.error;

  const result = {
    boardId: boardRow.id,
    id: taskRow.id,
    listId: groupRow.id,
    position: record.data.position,
    scope: wire,
    rev: out.rev,
    ...scopeReport(wire),
  };
  return stageBoardRecords(result, target.scopeKey, [toWireRecord(record)]);
}
